import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import * as React from 'react';
import type { InterviewDetailsSource } from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/resolveInterviewDetail';
import StepRetryNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/StepRetryNotifications';
import InterviewsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewsNotifications';
import InterviewDetailsContent from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/InterviewDetailsContent';
import DeleteInterviewDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/ConfirmationDialogs/DeleteInterviewDialog';
import InterviewDetailsConfirmationDialogs from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/ConfirmationDialogs/InterviewDetailsConfirmationDialogs';
import SpeakerAssignmentSidebar from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar';
import { useNavigate } from 'react-router-dom';
import { useSelector } from '@verclam/synapse';
import Container from '@Guidelines/Component/Container';
import SubjectBreadcrumb from '@UI/Pages/Beneficiaries/Shared/SubjectBreadcrumb';
import useSubjectAddressParams from '@UI/Pages/Beneficiaries/Hooks/Params/useSubjectAddressParams';
import useInterviewIdParams from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/Hooks/useInterviewIdParams';

/** `unknown` covers the moment before the interview reaches the store. */
const BREADCRUMB_ITEMS: Readonly<Record<InterviewKind | 'unknown', readonly { label: string }[]>> =
	Object.freeze({
		[InterviewKind.CONVERSATION]: [{ label: 'Entretien' }],
		[InterviewKind.NOTES]: [{ label: 'Prise de notes' }],
		[InterviewKind.TEXT]: [{ label: 'Texte saisi' }],
		unknown: [{ label: 'Enregistrement' }],
	});

/**
 * Mounted from the route of a server interview (`…/:dossier/interviews/:interview`);
 * an interview known only to this device has no page of its own, its route leads to the transfer
 * page. Validates every segment before mounting `InterviewDetailsContent` — an invalid dossier address
 * redirects to the beneficiary list, an invalid `interview` to that dossier.
 *
 * Also mounts the two hosts the transcript review block and its speaker-assignment sidebar depend
 * on: `SpeakerAssignmentSidebar` (opened by `speakerAssignmentSignal`, never by a prop) and
 * `InterviewDetailsConfirmationDialogs`, a single bare `<ConfirmDialog />` shared by every
 * impersonated confirmation on this page — mounting a second host anywhere else on this screen
 * would silently lose its calls.
 */
const InterviewDetails: React.FC = () => {
	const navigate = useNavigate();

	const { subject } = useSubjectAddressParams();
	const { interviewId, isInvalidSegment } = useInterviewIdParams();

	const knownKind = useSelector(
		(state) => state.interviewsStore.interviews?.findById(interviewId)?.knownKind ?? null,
	);
	const breadcrumbKey = knownKind ?? 'unknown';

	const interviewDetailsSource = React.useMemo<InterviewDetailsSource | null>(() => {
		if (subject === null || isInvalidSegment) {
			return null;
		}

		return interviewId ? { type: 'server', interviewId } : null;
	}, [subject, isInvalidSegment, interviewId]);

	React.useEffect(() => {
		if (subject === null) {
			navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
			return;
		}

		if (isInvalidSegment) {
			navigate(buildSubjectPath(subject));
		}
	}, [subject, isInvalidSegment, navigate]);

	return (
		<Container overflow={'hidden'}>
			{subject !== null && (
				<SubjectBreadcrumb subject={subject} items={BREADCRUMB_ITEMS[breadcrumbKey]} />
			)}
			{subject !== null && interviewDetailsSource && (
				<InterviewDetailsContent subject={subject} interviewDetailsSource={interviewDetailsSource} />
			)}
			<InterviewsNotifications />
			<StepRetryNotifications />
			<SpeakerAssignmentSidebar />
			<InterviewDetailsConfirmationDialogs />
			<DeleteInterviewDialog />
		</Container>
	);
};

export default InterviewDetails;
